import { ApiUserOrgRole } from './RecordTypes/ApiUserOrgSchema';

// *************************************************************************************
// Notification Registry
// *************************************************************************************
/**
 * Single source of truth for the org-configurable notifications surfaced in the
 * Org Settings → Workflow tab.
 *
 * To add a new notification:
 *   1. Add its key + definition below.
 *   2. Wire a server-side trigger that resolves the effective config via
 *      `resolveNotificationConfig(key, allOrgSettings)` before fanning out.
 *
 * Keys that are designed but not yet wired up are left commented out below —
 * uncomment when the corresponding trigger lands.
 *
 * Persistence: org-level overrides live under
 * `ApiAllOrgSettings.notificationSettings.overrides[key]`. Absence = the
 * defaults declared on the catalog entry are used.
 */

export type NotificationCategory =
  | 'propertyIngestion'
  | 'assignments'
  | 'scopeOfWork'
  | 'changeOrders'
  | 'progress'
  | 'closeOuts'
  | 'addendum';

/**
 * Priority describes how urgent the notification is and drives UI badge styling.
 *   - 'workflowBlocking': recipient cannot make forward progress without it
 *   - 'workflowRelevant': contextually important but not blocking
 *   - 'informational':    awareness only
 */
export type NotificationPriority = 'workflowBlocking' | 'workflowRelevant' | 'informational';

export type NotificationDefinition = {
  category: NotificationCategory;
  displayName: string;
  description: string;
  priority: NotificationPriority;
  defaultEnabled: boolean;
  defaultEmailEnabled: boolean;
  /** Roles that receive this notification by default. */
  defaultRecipientRoles: ApiUserOrgRole[];
  /** Roles that may receive this notification at all (others are hidden in the UI). */
  availableRecipientRoles: ApiUserOrgRole[];
  /**
   * If set, the corresponding control(s) are shown but not editable in the UI.
   *   - 'enabled': master on/off is locked
   *   - 'email': email toggle is locked
   *   - 'all': every control is locked
   */
  locked?: 'enabled' | 'email' | 'all';
};

const ALL_ROLES: ApiUserOrgRole[] = ['admin', 'manager', 'fieldUser'];

export const NOTIFICATION_CATALOG = {
  // -------------------------------------------------------------------
  // Property Ingestion
  // -------------------------------------------------------------------
  // TODO: not yet implemented
  // bulkUploadCompleted: {
  //   category: 'propertyIngestion',
  //   displayName: 'Bulk Upload Completed',
  //   description: 'Sent when a CSV property import finishes successfully. Includes count of properties added.',
  //   priority: 'informational',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin'],
  //   availableRecipientRoles: ALL_ROLES,
  // },
  // TODO: not yet implemented
  // bulkUploadCompletedWithErrors: {
  //   category: 'propertyIngestion',
  //   displayName: 'Bulk Upload Completed With Errors',
  //   description: 'Sent when a CSV property import finishes with failed rows. Failed rows are silently absent from the system — admin must remediate.',
  //   priority: 'workflowBlocking',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin'],
  //   availableRecipientRoles: ALL_ROLES,
  // },

  // -------------------------------------------------------------------
  // Assignments
  // -------------------------------------------------------------------
  projectAssigned: {
    category: 'assignments',
    displayName: 'Project Assignment or reassignment',
    description: 'Sent when a project is dispatched or transferred. The previous user is also notified on reassignment.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  projectReassigned: {
    category: 'assignments',
    displayName: 'Project Reassigned',
    description: 'Sent when a field user is added to an existing project mid-flight.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  projectUnassigned: {
    category: 'assignments',
    displayName: 'Project Unassigned',
    description: 'Sent when a field user is removed from a project mid-flight.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  turnPassed: {
    category: 'assignments',
    displayName: 'Turn Passed',
    description: 'Sent when a project turn is passed. Field User means the person receiving the turn.',
    priority: 'workflowBlocking',
    defaultEnabled: true,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  turnTaken: {
    category: 'assignments',
    displayName: 'Turn Taken',
    description: 'Sent when a project turn is taken. Field User means the previous turn holder.',
    priority: 'workflowRelevant',
    defaultEnabled: true,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  // TODO: not yet implemented
  // dispatchNotesAdded: {
  //   category: 'assignments',
  //   displayName: 'Dispatch Notes Added',
  //   description: 'Sent when an admin adds context notes at dispatch. Like instructions, access details or property-specific guidance.',
  //   priority: 'workflowRelevant',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['fieldUser'],
  //   availableRecipientRoles: ALL_ROLES,
  // },

  // -------------------------------------------------------------------
  // Scope of Work
  // -------------------------------------------------------------------
  scopeSubmittedForReview: {
    category: 'scopeOfWork',
    displayName: 'Scope Submitted for Review',
    description: 'Sent when a field user submits a scope of work for review. Approval is required before a PO is generated.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['admin', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  scopeEditRequest: {
    category: 'scopeOfWork',
    displayName: 'Scope Edit Request',
    description: 'Sent when a reviewer requests edits to a scope of work. Reviewer notes are required and are surfaced in the notification body.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  scopeApproved: {
    category: 'scopeOfWork',
    displayName: 'Scope Approved',
    description: 'Sent when an admin approves the scope of work. Work can begin and the PO is generated. Ops users must issue the SOW + PO to contractors.',
    priority: 'workflowRelevant',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  // TODO: not yet implemented
  // scopeSubmittedWithAttentionItems: {
  //   category: 'scopeOfWork',
  //   displayName: 'Scope Submitted with Attention Items',
  //   description: 'Sent when a scope is submitted with flags present (deferred items, unassigned contractors, missing categories, etc.). Context for the reviewer — no separate action required.',
  //   priority: 'workflowRelevant',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin', 'manager'],
  //   availableRecipientRoles: ALL_ROLES,
  // },

  // -------------------------------------------------------------------
  // Change Orders
  // -------------------------------------------------------------------
  changeOrderSubmittedByFieldUser: {
    category: 'changeOrders',
    displayName: 'Change Order Submitted By Field User',
    description:
      'Sent when a field user submits a change order. CO creation is the submission event — review and approval are required before financial impact.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['admin', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  // TODO: not yet implemented
  // changeOrderCreatedByAdmin: {
  //   category: 'changeOrders',
  //   displayName: 'Change Order Created By Admin',
  //   description: 'Sent when an admin initiates a change order. Field & ops users need to know the scope has changed.',
  //   priority: 'workflowRelevant',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['fieldUser', 'manager'],
  //   availableRecipientRoles: ALL_ROLES,
  // },
  changeOrderEditRequest: {
    category: 'changeOrders',
    displayName: 'Change Order Edit Request',
    description: 'Sent when a reviewer requests edits to a Change Order. Reviewer notes are required and are surfaced in the notification body.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser'],
    availableRecipientRoles: ALL_ROLES,
  },
  changeOrderApproved: {
    category: 'changeOrders',
    displayName: 'Change Order Approved',
    description: 'Sent when a change order is approved. Work can proceed and the PO is updated. Ops users must issue the updated PO to contractors.',
    priority: 'workflowRelevant',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },

  // -------------------------------------------------------------------
  // Progress & Punch List
  // -------------------------------------------------------------------
  // TODO: not yet implemented
  // reworkMarkedOnTask: {
  //   category: 'progress',
  //   displayName: 'Rework Marked on a Task',
  //   description: 'In-scope confirmation when a field user sets a task status to Rework. Task stays in Rework until updated.',
  //   priority: 'informational',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: false,
  //   defaultRecipientRoles: ['fieldUser'],
  //   availableRecipientRoles: ALL_ROLES,
  // },
  // TODO: not yet implemented (requires contractor portal)
  // punchListSentToContractor: {
  //   category: 'progress',
  //   displayName: 'Punch List Sent to Contractor',
  //   description: 'Sent when a punch list is issued to a contractor.',
  //   priority: 'workflowBlocking',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: [],
  //   availableRecipientRoles: ALL_ROLES,
  // },
  // TODO: not yet implemented (requires contractor portal)
  // contractorMarkedReworkComplete: {
  //   category: 'progress',
  //   displayName: 'Contractor Marked Rework Complete',
  //   description: 'Sent when a contractor updates rework status to complete in the contractor portal.',
  //   priority: 'workflowRelevant',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin', 'manager', 'opsUser', 'fieldUser'],
  //   availableRecipientRoles: ALL_ROLES,
  // },

  // -------------------------------------------------------------------
  // Close Outs
  // -------------------------------------------------------------------
  closeOutSubmitted: {
    category: 'closeOuts',
    displayName: 'Close Out Submitted',
    description: 'Sent when a field user submits a close out. Review is required before final approval; all rework must be resolved before submission.',
    priority: 'workflowBlocking',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['admin', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  closeOutApproved: {
    category: 'closeOuts',
    displayName: 'Close Out Approved',
    description: 'Sent when a close out is approved. The project moves to "Closed" status.',
    priority: 'workflowRelevant',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['fieldUser', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  // Edit requests on a close out cause the scan to transition into a change
  // order, so the changeOrderEditRequest notification fires instead. Keeping
  // this entry around as a placeholder for the future, but disabling it for now.
  // closeOutEditRequest: {
  //   category: 'closeOuts',
  //   displayName: 'Close Out Edit Request',
  //   description: 'Sent when a reviewer requests edits to a close out. Reviewer notes are required and are surfaced in the notification body.',
  //   priority: 'workflowBlocking',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['fieldUser'],
  //   availableRecipientRoles: ALL_ROLES,
  // },

  // -------------------------------------------------------------------
  // Addendum
  // -------------------------------------------------------------------
  projectReactivatedForAddendum: {
    category: 'addendum',
    displayName: 'Project Reactivated for Addendum',
    description: 'Sent when a closed project is reactivated. The close out becomes outdated and the scope returns to Current.',
    priority: 'workflowRelevant',
    defaultEnabled: false,
    defaultEmailEnabled: true,
    defaultRecipientRoles: ['admin', 'manager'],
    availableRecipientRoles: ALL_ROLES,
  },
  // TODO: not yet implemented (same flow as changeOrderSubmittedByFieldUser)
  // addendumChangeOrderSubmitted: {
  //   category: 'addendum',
  //   displayName: 'Addendum Change Order Submitted',
  //   description: 'Sent when a field user submits an addendum change order. Same flow as a standard CO submission.',
  //   priority: 'workflowBlocking',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin', 'manager'],
  //   availableRecipientRoles: ALL_ROLES,
  // },
  // TODO: not yet implemented (same flow as closeOutSubmitted)
  // addendumCloseOutSubmitted: {
  //   category: 'addendum',
  //   displayName: 'Addendum Close Out Submitted',
  //   description: 'Sent when a field user submits the second close out for an addendum. Same flow as a standard close out submission.',
  //   priority: 'workflowBlocking',
  //   defaultEnabled: true,
  //   defaultEmailEnabled: true,
  //   defaultRecipientRoles: ['admin', 'manager'],
  //   availableRecipientRoles: ALL_ROLES,
  // },
} as const satisfies Record<string, NotificationDefinition>;

export type NotificationKey = keyof typeof NOTIFICATION_CATALOG;

export const NOTIFICATION_KEYS = Object.keys(NOTIFICATION_CATALOG) as NotificationKey[];

export const NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  'propertyIngestion',
  'assignments',
  'scopeOfWork',
  'changeOrders',
  'progress',
  'closeOuts',
  'addendum',
];

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, { title: string; description: string }> = {
  propertyIngestion: {
    title: 'Property Ingestion',
    description: 'Outcomes of bulk CSV property uploads. Surfaces row-level failures so admins can remediate.',
  },
  assignments: {
    title: 'Assignments',
    description: 'Ensures users know when work is assigned and have context before arriving on site.',
  },
  scopeOfWork: {
    title: 'Scope Of Work',
    description: 'Scope of Work submission, review, and approval. Approval generates purchase orders & authorizes work to begin.',
  },
  changeOrders: {
    title: 'Change Orders',
    description:
      'Covers changes to an approved scope. Can be initiated by a field user or admin. Requires approval before impacting financials or updating the Purchase Order.',
  },
  progress: {
    title: 'Progress & Punch List',
    description: 'Tracks in-progress rework and punch list hand-offs between field users and contractors.',
  },
  closeOuts: {
    title: 'Close Outs',
    description:
      'Defines the final approval chain to close a project. Can only be submitted after all rework items are resolved. Approval moves the project to "Closed" status.',
  },
  addendum: {
    title: 'Addendum',
    description:
      'Covers project reactivation. An addendum reopens a closed project for additional work, typically due to buyer or tenant requests. The original close out becomes outdated; the scope returns to Current.',
  },
};

export const getNotificationKeysByCategory = (category: NotificationCategory): NotificationKey[] =>
  NOTIFICATION_KEYS.filter(key => NOTIFICATION_CATALOG[key].category === category);

/**
 * Resolve a notification's effective config by merging catalog defaults with org-level overrides.
 * Use this on both client and server to get the current effective values for a notification key.
 */
export type ResolvedNotificationConfig = {
  key: NotificationKey;
  enabled: boolean;
  emailEnabled: boolean;
  recipientRoles: ApiUserOrgRole[];
};

export const resolveNotificationConfig = (
  key: NotificationKey,
  allOrgSettingsOrEmpty: { notificationSettings?: { overrides?: Record<string, any> } } | any,
): ResolvedNotificationConfig => {
  const def = NOTIFICATION_CATALOG[key];
  const override = allOrgSettingsOrEmpty?.notificationSettings?.overrides?.[key];
  return {
    key,
    enabled: override?.enabled ?? def.defaultEnabled,
    emailEnabled: override?.emailEnabled ?? def.defaultEmailEnabled,
    recipientRoles: (override?.recipientRoles ?? def.defaultRecipientRoles) as ApiUserOrgRole[],
  };
};
